<script setup>
// The Social posts block's body, shared by both templates. Two sources:
//
//  - posts: links a manager pasted, drawn with each platform's embed;
//  - youtube: a channel's newest videos, read from its public feed by
//    the server (YoutubeFeed) and drawn in the site's own style.
//
// Instagram and Facebook have no "newest posts" without an approved
// Meta app, which is why they are posts-only for now.
import { computed, inject, onMounted, ref, shallowRef, watch } from 'vue';
import SocialPost from './SocialPost.vue';
import { onCanonicalHost, sitePrefix } from './useSiteForm.js';
import { socialFeedColumns, socialFeedSource, socialPostItems, youtubeFeedUrl } from './socialPosts.js';

const props = defineProps({
    config: { type: Object, required: true },
    org: { type: Object, default: () => ({}) },
});
// A channel whose feed failed or has no videos tells the block, which
// then draws nothing for a visitor rather than a heading over nothing.
const emit = defineEmits(['empty']);

const editing = inject('tiqEditorCanvas', false);

const source = computed(() => socialFeedSource(props.config));
const posts = computed(() => socialPostItems(props.config));
const columns = computed(() => socialFeedColumns(props.config));

// ── YouTube ────────────────────────────────────────────────────────
const feed = shallowRef(null);
const feedState = ref('idle'); // idle | loading | ready | error
const feedMessage = ref('');
const playingId = ref(null);
const feedUrl = computed(() => (source.value === 'youtube' ? youtubeFeedUrl({
    orgSlug: props.org?.slug,
    channel: props.config.youtube_channel,
    count: props.config.count,
    canonical: editing || onCanonicalHost(),
    hostPrefix: sitePrefix(),
}) : null));

let seq = 0;
async function loadFeed() {
    const url = feedUrl.value;
    if (!url) { feed.value = null; feedState.value = 'idle'; return; }
    const mine = ++seq;
    feedState.value = 'loading';
    try {
        const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        const body = await res.json().catch(() => ({}));
        if (mine !== seq) return;
        if (!res.ok) throw new Error(body.message || '');
        feed.value = body;
        feedState.value = 'ready';
        emit('empty', !(body?.videos?.length));
    } catch (e) {
        if (mine !== seq) return;
        feed.value = null;
        feedMessage.value = e?.message || 'The videos could not be loaded.';
        feedState.value = 'error';
        emit('empty', true);
    }
}
onMounted(loadFeed);
let debounce = null;
watch(feedUrl, () => {
    clearTimeout(debounce);
    debounce = setTimeout(loadFeed, 500);
});

function dateLabel(iso) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
</script>

<template>
    <div class="tiq-ssf" :class="`tiq-ssf--cols-${columns}`">
        <template v-if="source === 'posts'">
            <div v-if="posts.length" class="tiq-ssf__grid">
                <SocialPost v-for="p in posts" :key="`${p.platform}:${p.id}`" :post="p" />
            </div>
            <p v-else-if="editing" class="tiq-ssf__hint">Paste links to posts in the editor: Instagram, Facebook, TikTok, YouTube or X.</p>
        </template>

        <template v-else>
            <div v-if="feedState === 'ready' && feed?.videos?.length" class="tiq-ssf__grid">
                <article v-for="v in feed.videos" :key="v.id" class="tiq-ssf__video">
                    <div class="tiq-ssf__frame">
                        <iframe v-if="playingId === v.id" class="tiq-ssf__player"
                                :src="`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`"
                                :title="v.title" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>
                        <button v-else type="button" class="tiq-ssf__poster" :aria-label="`Play ${v.title}`" @click="playingId = v.id">
                            <img :src="v.thumb" alt="" loading="lazy" />
                            <span class="tiq-ssf__play" aria-hidden="true"><svg viewBox="0 0 24 24" width="24" height="24"><path d="M8 5v14l11-7z" fill="currentColor"/></svg></span>
                        </button>
                    </div>
                    <h3 class="tiq-ssf__title">{{ v.title }}</h3>
                    <p class="tiq-ssf__date">{{ dateLabel(v.published) }}</p>
                </article>
            </div>
            <p v-if="feedState === 'ready' && feed?.url" class="tiq-ssf__more">
                <a :href="feed.url" target="_blank" rel="noopener">More on YouTube →</a>
            </p>
            <p v-else-if="editing && feedState === 'idle'" class="tiq-ssf__hint">Enter your YouTube channel link or @handle in the editor.</p>
            <p v-else-if="editing && feedState === 'error'" class="tiq-ssf__hint">{{ feedMessage }}</p>
            <p v-else-if="feedState === 'loading'" class="tiq-ssf__hint">Loading videos…</p>
        </template>
    </div>
</template>

<style scoped>
.tiq-ssf { --ssf-min: 300px; }
.tiq-ssf--cols-2 { --ssf-min: 420px; }
.tiq-ssf--cols-4 { --ssf-min: 230px; }
.tiq-ssf__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--ssf-min)), 1fr));
    gap: clamp(16px, 2.5vw, 28px);
    align-items: start;
}
.tiq-ssf__hint { margin: 0; font-style: italic; color: var(--ssf-muted, #6b7280); }
.tiq-ssf__video { min-width: 0; }
.tiq-ssf__frame {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--ssf-radius, 8px);
    background: #000;
}
.tiq-ssf__player, .tiq-ssf__poster { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.tiq-ssf__poster { padding: 0; cursor: pointer; background: #000; }
.tiq-ssf__poster img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tiq-ssf__play {
    position: absolute;
    left: 12px;
    bottom: 12px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--ssf-accent, #1a1a1a);
    color: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
}
.tiq-ssf__title {
    margin: 12px 0 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.tiq-ssf__date { margin: 4px 0 0; font-size: 13px; color: var(--ssf-muted, #6b7280); }
.tiq-ssf__more { margin: 20px 0 0; font-weight: 600; }
.tiq-ssf__more a { color: var(--ssf-accent, inherit); }
</style>
